/**
 * Phase 2.4 — Telecommunications Infrastructure Security results section.
 *
 * One section per assessed module (C1–C8). Per module: a 2-column
 * Control × Status table, followed by a per-control narrative for any
 * control that is Partially Implemented or Not Implemented.
 *
 * Pulls data from `reviewer_get_telecom_report_bundle`. Renders ONLY
 * modules whose controls appear in the bundle (driven by
 * components_assessed in assessment_infrastructure_scope).
 *
 * Scoring-neutral.
 */
import type jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PAGE_MARGIN, REPORT_THEME } from "./theme";
import { drawRunningHeader, drawPageFooter, drawSectionTitle } from "./pageChrome";
import {
  INFRASTRUCTURE_CONTROL_METADATA,
  resolveInfrastructureControlDisplay,
  type ModuleId,
} from "@/configs/controlGuidance/infrastructureControlMetadata";
import { buildInfrastructureControlNarrative } from "@/reports/telecom/infrastructureNarratives";

export interface TelecomBundleControl {
  control_key: string;
  control_label: string;
  category: string | null;
  status: string | null;
  reviewer_notes: string | null;
  nist_control_code: string | null;
  nist_function: string | null;
}

export interface TelecomReportBundle {
  scope: {
    telecom_service_model?: string[] | null;
    components_present?: string[] | null;
    components_assessed?: string[] | null;
    scope_notes?: string | null;
    score_basis_version?: string | null;
  } | null;
  controls: TelecomBundleControl[];
  counts: {
    controls_evaluated: number;
    implemented: number;
    partial: number;
    not_implemented: number;
    findings_count: number;
  } | null;
}

const MODULE_LABELS: Record<ModuleId, string> = {
  B1: "B1 · Physical Facility Security",
  B2: "B2 · Environmental Controls",
  B3: "B3 · Power & Resilience",
  B4: "B4 · Network Infrastructure",
  C1: "C1 · Radio Access Network",
  C2: "C2 · Core Network",
  C3: "C3 · Signaling Network",
  C4: "C4 · Transport / Backhaul",
  C5: "C5 · Virtualization & Edge",
  C6: "C6 · OSS / BSS",
  C7: "C7 · Subscriber / Roaming / IoT",
  C8: "C8 · Service Continuity",
};

const MODULE_ORDER: ModuleId[] = ["C1", "C2", "C3", "C4", "C5", "C6", "C7", "C8"];

export interface TelecomResultsContext {
  bundle: TelecomReportBundle | null;
  clientOrgName: string;
  methodology?: string | null;
  frameworks?: string[] | null;
  startPageNumber: number;
  /** Override the rendered section number. Defaults to "06" for backward compatibility. */
  sectionNumber?: string;
}

/** Returns the last page number used, or startPageNumber - 1 when skipped. */
export function renderTelecomResults(
  doc: jsPDF,
  ctx: TelecomResultsContext,
): number {
  const bundle = ctx.bundle;
  if (!bundle || !bundle.controls || bundle.controls.length === 0) {
    return ctx.startPageNumber - 1;
  }

  const grouped = groupByModule(bundle.controls);
  const modulesToRender = MODULE_ORDER.filter((m) => grouped.get(m)?.length);
  if (modulesToRender.length === 0) return ctx.startPageNumber - 1;

  let page = ctx.startPageNumber - 1;

  // Section opener
  page += 1;
  doc.addPage();
  drawRunningHeader(doc, {
    clientName: ctx.clientOrgName,
    frameworks: ctx.frameworks ?? null,
    methodology: ctx.methodology ?? null,
  });
  let y = drawSectionTitle(doc, {
    sectionNumber: ctx.sectionNumber ?? "06",
    title: "Telecommunications Infrastructure Security",
  });

  doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  const pageW = doc.internal.pageSize.getWidth();
  const intro =
    "The following results summarize the telecom-specific infrastructure controls reviewed during this engagement. Modules are listed in operator-layer order; only modules whose components were marked Assessed in the engagement scope are shown. Findings derived from this section are merged into the consolidated Findings, Remediation Roadmap and Corrective Action Plan elsewhere in this report.";
  const introLines = doc.splitTextToSize(intro, pageW - PAGE_MARGIN * 2);
  doc.text(introLines, PAGE_MARGIN, y, { lineHeightFactor: 1.45 } as any);
  y += introLines.length * 14 + 12;

  const pageH = doc.internal.pageSize.getHeight();
  const footerReserve = 70;

  for (const moduleId of modulesToRender) {
    const rows = grouped.get(moduleId) ?? [];
    const moduleLabel = MODULE_LABELS[moduleId] ?? moduleId;

    // Estimate required height; break to new page when tight.
    const estTableH = 40 + rows.length * 22;
    const narratives = rows
      .map((r) => ({
        // Phase 2.8.1 — Never expose raw control_key in PDF output.
        label: resolveInfrastructureControlDisplay(r.control_key, r.control_label).title,
        narrative: buildInfrastructureControlNarrative(r.control_key, r.status),
        status: (r.status ?? "").trim(),
      }))
      .filter((n) => n.narrative && (n.status.toLowerCase() === "partially implemented" || n.status.toLowerCase() === "not implemented" || n.status.toLowerCase() === "partial"));
    const estNarrativeH = narratives.length * 36;
    const blockH = 30 + estTableH + estNarrativeH + 18;

    if (y + blockH > pageH - footerReserve && y > 120) {
      drawPageFooter(doc, page);
      page += 1;
      doc.addPage();
      drawRunningHeader(doc, {
        clientName: ctx.clientOrgName,
        frameworks: ctx.frameworks ?? null,
        methodology: ctx.methodology ?? null,
      });
      y = drawSectionTitle(doc, {
        sectionNumber: ctx.sectionNumber ?? "06",
        title: "Telecommunications Infrastructure Security",
        continued: true,
      });
      y += 8;
    }

    // Module heading
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...REPORT_THEME.navy.rgb);
    doc.text(moduleLabel, PAGE_MARGIN, y + 14);
    y += 22;

    autoTable(doc, {
      startY: y,
      margin: { left: PAGE_MARGIN, right: PAGE_MARGIN, bottom: footerReserve },
      head: [["Control", "Status"]],
      body: rows.map((r) => [
        // Phase 2.8.1 — Always render the resolved human title, never the raw control_key.
        resolveInfrastructureControlDisplay(r.control_key, r.control_label).title,
        (r.status ?? "—").trim() || "—",
      ]),
      theme: "grid",
      styles: {
        font: "helvetica",
        fontSize: 9.5,
        cellPadding: { top: 6, right: 8, bottom: 6, left: 8 },
        textColor: REPORT_THEME.textPrimary.rgb as any,
        lineColor: REPORT_THEME.border.rgb as any,
        lineWidth: 0.4,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: REPORT_THEME.surfaceAlt.rgb as any,
        textColor: REPORT_THEME.navy.rgb as any,
        fontStyle: "bold",
        fontSize: 9.5,
        halign: "left",
      },
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { cellWidth: 140 },
      },
    });
    y = (doc as any).lastAutoTable?.finalY ?? y + 40;
    y += 8;

    // Per-control narratives for Partial / Not Implemented
    if (narratives.length > 0) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
      for (const n of narratives) {
        const lines = doc.splitTextToSize(`• ${n.narrative}`, pageW - PAGE_MARGIN * 2 - 12);
        if (y + lines.length * 12 > pageH - footerReserve) {
          drawPageFooter(doc, page);
          page += 1;
          doc.addPage();
          drawRunningHeader(doc, {
            clientName: ctx.clientOrgName,
            frameworks: ctx.frameworks ?? null,
            methodology: ctx.methodology ?? null,
          });
          y = drawSectionTitle(doc, {
            sectionNumber: ctx.sectionNumber ?? "06",
            title: "Telecommunications Infrastructure Security",
            continued: true,
          }) + 8;
        }
        doc.text(lines, PAGE_MARGIN + 4, y, { lineHeightFactor: 1.4 } as any);
        y += lines.length * 12 + 4;
      }
      y += 6;
    } else {
      y += 6;
    }
  }

  drawPageFooter(doc, page);
  return page;
}

function groupByModule(controls: TelecomBundleControl[]): Map<ModuleId, TelecomBundleControl[]> {
  const out = new Map<ModuleId, TelecomBundleControl[]>();
  for (const c of controls) {
    const meta = INFRASTRUCTURE_CONTROL_METADATA[c.control_key];
    if (!meta) continue;
    const m = meta.module;
    // Phase 2.7B — Option C: telecom_v2 reports never render legacy
    // generic (B1–B4) infrastructure controls, even if the bundle
    // happens to surface them through historical data.
    if (m.startsWith("B")) continue;
    if (!out.has(m)) out.set(m, []);
    out.get(m)!.push(c);
  }
  return out;
}
